/**
 * Pure crop geometry for the CORE-583 cover cropper.
 *
 * Kept in its own module — free of react-native imports — so the maths can be
 * unit-tested without a DOM or a RN test environment. `CoverCropModal` is the
 * only consumer.
 */

/**
 * The cover's aspect ratio — the single source of truth. The crop frame, the
 * step-3 preview card and the exported file all derive from this constant, and
 * they have to: CORE-739 was filed because they didn't. The step showed the
 * finished 16:9 crop inside a 574×218 (≈2.63:1) box with `cover`, so the card
 * silently re-cropped 16% off the top and 16% off the bottom and the master saw
 * a narrower strip than the one they had just chosen.
 */
export const COVER_ASPECT = 16 / 9;

/** Widest the on-screen crop frame goes; it shrinks with the sheet below this. */
export const FRAME_W = 560;
export const FRAME_H = Math.round(FRAME_W / COVER_ASPECT);

/**
 * Frame height for a given width. The frame used to be `width: '100%'` with a
 * fixed height, so on any viewport narrower than {@link FRAME_W} it stopped
 * being 16:9 while the crop maths kept assuming it was — what you saw was not
 * what you got. Every consumer derives the height from the width instead.
 */
export function frameHeightFor(width: number): number {
    return width / COVER_ASPECT;
}

/** Export size. 1280×720 keeps a hero legible without shipping a
 *  multi-megabyte file for what is usually a card image. */
export const OUT_W = 1280;
export const OUT_H = Math.round(OUT_W / COVER_ASPECT);

export interface Offset { x: number; y: number }

/** «Cover» fit — the smallest scale at which the image still fills the frame,
 *  so no empty gutter can ever end up inside the crop. */
export function coverFitScale(imgW: number, imgH: number, frameW = FRAME_W, frameH = FRAME_H): number {
    return Math.max(frameW / imgW, frameH / imgH);
}

/** Keep the frame covered: offsets are ≤ 0 and never expose an edge. */
export function clampOffset(
    o: Offset, dispW: number, dispH: number, frameW = FRAME_W, frameH = FRAME_H,
): Offset {
    return {
        x: Math.min(0, Math.max(frameW - dispW, o.x)),
        y: Math.min(0, Math.max(frameH - dispH, o.y)),
    };
}

/** Frame → source-pixel rect in the original image's coordinates. */
export function cropSourceRect(
    offset: Offset, scale: number, frameW = FRAME_W, frameH = FRAME_H,
): { sx: number; sy: number; sw: number; sh: number } {
    return { sx: -offset.x / scale, sy: -offset.y / scale, sw: frameW / scale, sh: frameH / scale };
}

/** Zoom about the frame's centre: the source point under the midpoint stays
 *  under the midpoint, so zooming pulls toward what the master is looking at. */
export function zoomAnchoredOffset(
    offset: Offset, prevScale: number, nextScale: number, frameW = FRAME_W, frameH = FRAME_H,
): Offset {
    const cx = frameW / 2, cy = frameH / 2;
    const sx = (cx - offset.x) / prevScale;
    const sy = (cy - offset.y) / prevScale;
    return { x: cx - sx * nextScale, y: cy - sy * nextScale };
}
